// Ported from LC2 (src/commons/components/HomeTabs/Esg/EsgComponent.js) — same block
// gating (first_block -> FinancialUnlocks, second_block -> StakerInsights, third_block ->
// EsgMetrics) and the same loading/success/empty states (isSuccess && (data || isStakeAdmin)
// shows content, otherwise the empty state) — see the comment on the Staker Insights block
// gating below for the one deliberate deviation from LC2. Visual layer matches the actual
// Figma frames (node 18158:8610 "Dashboard-Body" for the loaded state, node 18159:8843
// "ESG - Empty State" for the empty one): each block renders as its own bordered card (see
// FinancialUnlocks.js/StakerInsights.js/EsgMetrics.js) stacked with a 16px gap, with a
// "Page Header" (node 18158:8611) above them — shown only once there's real content, since
// the empty-state frame has no header at all. That header now has real Export (ExportMenu.js
// — links to fixed, pre-generated PDF/XLSX report files, not built from `data`) and
// date-range (DateRangeSelector.js — new, see its own header comment for why it doesn't
// filter data yet) controls, matching the Figma "ESG - Export Dropdown Open" /
// date-range-combobox frames instead of a static button.
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import {Box, Stack, Typography} from '@mui/material';

import FinancialUnlocks from './FinancialUnlocks';
import StakerInsights from './StakerInsights';
import EsgMetrics from './EsgMetrics';
import ExportMenu from './ExportMenu';
import DateRangeSelector from './DateRangeSelector';
import {getIsStakeAdmin} from '../../../app/store/reducers/session';
import {tokens} from '../../../themes/ThemeCustomization';
import emptyStateIcon from '../../../assets/esg/empty-state.svg';

// Figma: "ESG - Empty State" (node 18159:8843) — same "no records" card shown by the other
// ported sections (e.g. Communities), sized to this page's content instead.
const EsgEmptyState = () => (
    <Box
        sx={{
            border: `1px solid ${tokens.color.neutral200}`,
            borderRadius: '6px',
            backgroundColor: tokens.color.white,
            p: 2.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            rowGap: 3,
            minHeight: '300px'
        }}
    >
        <Box
            sx={{
                border: `1px solid ${tokens.color.neutral200}`,
                borderRadius: '6px',
                p: 2,
                display: 'flex'
            }}
        >
            <Box component="img" src={emptyStateIcon} alt="" sx={{width: 24, height: 24}} />
        </Box>
        <Stack rowGap={0.25} alignItems="center">
            <Typography variant="body2" fontWeight={500} textAlign="center">
                No records found
            </Typography>
            <Typography variant="caption" color="text.secondary" textAlign="center">
                There are no ESG records to display.
            </Typography>
        </Stack>
    </Box>
);

const EsgComponent = ({data, isLoading, isSuccess}) => {
    const isStakeAdmin = useSelector(getIsStakeAdmin);
    const [firstBlock, setFirstBlock] = useState(null);
    const [secondBlock, setSecondBlock] = useState(null);
    const [thirdBlock, setThirdBlock] = useState(null);

    useEffect(() => {
        if (data?.is_esg_enabled === false) {
            setFirstBlock(null);
            setSecondBlock(null);
            setThirdBlock(null);
            return;
        }

        setFirstBlock(data?.first_block?.rows?.length > 0 ? data.first_block : null);
        // Unlike first/third block, `second_block` (Staker Insights) is real-world often
        // returned with an empty `rows` array — the backend (esg_dashboard_routes.py's
        // find_staker_insights) only includes a row per metric that clears a 50% portfolio
        // threshold, so many accounts legitimately qualify for zero of the five. Hiding the
        // whole card in that case made it look broken/missing against the Figma design,
        // which always shows the card — so this now keeps the card (title + description)
        // whenever the backend sends the block at all, and StakerInsights.js renders an
        // inline "not enough data yet" state instead of blank cells when rows is empty.
        setSecondBlock(data?.second_block ? data.second_block : null);
        setThirdBlock(data?.third_block?.rows?.length > 0 ? data.third_block : null);
    }, [isLoading, data]);

    if (isLoading) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 4}}>
                Loading ESG data…
            </Typography>
        );
    }

    const hasContent = Boolean(firstBlock || secondBlock || thirdBlock);

    if (isSuccess && (data || isStakeAdmin) && hasContent) {
        return (
            <Stack direction="column" rowGap={2}>
                <Stack direction="row" justifyContent="flex-end" columnGap={1}>
                    <DateRangeSelector />
                    <ExportMenu />
                </Stack>
                {firstBlock && <FinancialUnlocks key={firstBlock.title} data={firstBlock} />}
                {secondBlock && <StakerInsights key={secondBlock.title} data={secondBlock} />}
                {thirdBlock && <EsgMetrics key={thirdBlock.title} data={thirdBlock} />}
            </Stack>
        );
    }

    return <EsgEmptyState />;
};

EsgComponent.propTypes = {
    data: PropTypes.object,
    isLoading: PropTypes.bool,
    isSuccess: PropTypes.bool
};

export default EsgComponent;
